import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export const SEOOptimizer = () => {
  const location = useLocation();

  // Enhanced bot detection including AI bots
  const isBot = () => {
    if (typeof navigator === 'undefined') return true;
    const userAgent = navigator.userAgent.toLowerCase();
    return /bot|crawler|spider|crawling|facebookexternalhit|twitterbot|linkedinbot|whatsapp|telegram|claude|gpt|openai|anthropic|gemini|bard/.test(userAgent);
  };

  // Log bot detection for monitoring
  const logBotAccess = () => {
    if (isBot()) {
      console.log('Bot detected on main site:', navigator.userAgent, 'Path:', location.pathname);
      
      // Track bot access
      try {
        // Send to analytics if available
        if (typeof window !== 'undefined' && (window as any).gtag) {
          (window as any).gtag('event', 'bot_access', {
            'user_agent': navigator.userAgent.substring(0, 100),
            'page_path': location.pathname,
            'timestamp': new Date().toISOString()
          });
        }
      } catch (error) {
        console.log('Bot tracking failed:', error);
      }
    }
  };

  useEffect(() => {
    // Přidej JSON-LD strukturovaná data dynamicky podle stránky
    const addStructuredData = (data: any) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(data);
      script.id = 'dynamic-structured-data';
      
      // Odstraň předchozí strukturovaná data
      const existing = document.getElementById('dynamic-structured-data');
      if (existing) {
        existing.remove();
      }
      
      document.head.appendChild(script);
    };

    // Add bot-specific meta tags
    const addBotMetaTags = () => {
      // Add meta tags for better bot crawling
      const metaTags = [
        { name: 'robots', content: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1' },
        { name: 'googlebot', content: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1' },
        { name: 'bingbot', content: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1' },
        { property: 'fb:app_id', content: 'horsebeat' },
        { name: 'twitter:site', content: '@horsebeat' },
        { name: 'apple-mobile-web-app-capable', content: 'yes' },
        { name: 'mobile-web-app-capable', content: 'yes' }
      ];

      metaTags.forEach(({ name, property, content }) => {
        const existingTag = document.querySelector(`meta[${name ? 'name' : 'property'}="${name || property}"]`);
        if (!existingTag) {
          const meta = document.createElement('meta');
          if (name) meta.name = name;
          if (property) meta.setAttribute('property', property);
          meta.content = content;
          document.head.appendChild(meta);
        }
      });
    };

    // Add static content for bots that can't execute JavaScript
    const addStaticContent = () => {
      if (isBot()) {
        const noscriptDiv = document.getElementById('static-content');
        if (!noscriptDiv) {
          const staticContent = document.createElement('noscript');
          staticContent.id = 'static-content';
          staticContent.innerHTML = `
            <div style="padding: 20px; text-align: center; font-family: Arial, sans-serif;">
              <h1>Horsebeat.cz - Dostihové kurzy a statistiky</h1>
              <p>Kompletní databáze dostihových koní, žokejů a výsledků závodů.</p>
              <ul style="list-style: none; text-align: left; max-width: 400px; margin: 0 auto;">
                <li><a href="/horses">🏇 Databáze koní</a></li>
                <li><a href="/jockeys">👤 Profily žokejů</a></li>
                <li><a href="/race-results">🏁 Výsledky dostihů</a></li>
                <li><a href="/odds-analysis">📊 Analýza kurzů</a></li>
              </ul>
              <p>Pro plnou funkcionalitu povolte JavaScript.</p>
            </div>
          `;
          document.body.appendChild(staticContent);
        }
      }
    };

    addBotMetaTags();
    addStaticContent();
    logBotAccess();

    // Generuj strukturovaná data podle aktuální stránky
    const pathname = location.pathname;
    
    if (pathname === '/horses') {
      addStructuredData({
        "@context": "https://schema.org",
        "@type": "ItemList",
        "name": "Dostihové koně - Kompletní databáze",
        "description": "Přehled všech dostihových koní s podrobnými statistikami, výkony a historií závodů.",
        "url": `https://horsebeat.cz${pathname}`,
        "numberOfItems": "500+",
        "itemListElement": {
          "@type": "ListItem",
          "name": "Dostihový kůň",
          "description": "Informace o dostihových koních včetně statistik a výkonů"
        }
      });
    } else if (pathname === '/jockeys') {
      addStructuredData({
        "@context": "https://schema.org",
        "@type": "ItemList",
        "name": "Žokejové - Profily a statistiky",
        "description": "Komplexní databáze žokejů s jejich úspěšností, statistikami a kariérními údaji.",
        "url": `https://horsebeat.cz${pathname}`,
        "numberOfItems": "200+",
        "itemListElement": {
          "@type": "ListItem",
          "name": "Žokej",
          "description": "Profily žokejů s podrobnými statistikami úspěšnosti"
        }
      });
    } else if (pathname === '/race-results') {
      addStructuredData({
        "@context": "https://schema.org",
        "@type": "SportsEvent",
        "name": "Výsledky dostihů",
        "description": "Aktuální a historické výsledky dostihů z českých hipodromů.",
        "url": `https://horsebeat.cz${pathname}`,
        "sport": "Dostihový sport",
        "location": {
          "@type": "Place",
          "name": "České hipodrómy",
          "address": {
            "@type": "PostalAddress",
            "addressCountry": "CZ"
          }
        }
      });
    } else if (pathname === '/odds-analysis') {
      addStructuredData({
        "@context": "https://schema.org",
        "@type": "AnalysisNewsArticle",
        "headline": "Analýza dostihových kurzů",
        "description": "Podrobné analýzy dostihových kurzů, srovnání sázkových kanceláří a tipy na sázení.",
        "url": `https://horsebeat.cz${pathname}`,
        "author": {
          "@type": "Organization",
          "name": "Horsebeat.cz"
        },
        "publisher": {
          "@type": "Organization",
          "name": "Horsebeat.cz",
          "url": "https://horsebeat.cz"
        }
      });
    } else if (pathname === '/chov') {
      addStructuredData({
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "Projekt podpory českého plnokrevníka",
        "description": "Oživení chovu českých plnokrevníků v návaznosti na tradici hřebčína v Napajedlech",
        "url": `https://horsebeat.cz${pathname}`,
        "foundingDate": "2025",
        "areaServed": "CZ",
        "knowsAbout": ["Chov plnokrevných koní", "Dostihový sport", "Chovatelství koní"],
        "offers": {
          "@type": "OfferCatalog",
          "name": "Členství v projektu chovu",
          "itemListElement": [
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Příležitostný fanoušek",
                "description": "Základní členství s přístupem k obsahu a novinkám"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Aktivní žokej",
                "description": "Plné členství s hlasovacím právem a prémiovými benefity"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Spoluvlastník",
                "description": "Prémiové členství s podílem na výnosech a exkluzivními benefity"
              }
            }
          ]
        }
      });
    }

    // Přidej preload linky pro důležité stránky
    const preloadLinks = [
      '/horses',
      '/jockeys', 
      '/race-results',
      '/odds-analysis'
    ];

    preloadLinks.forEach(path => {
      if (path !== pathname) {
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = `https://horsebeat.cz${path}`;
        document.head.appendChild(link);
      }
    });

  }, [location.pathname]);

  return null; // Komponenta nic nevykresluje
};